/**
 * The words of the notices that ship with the app.
 *
 * A `BUILTIN` message is a database row that carries placement and rank — an
 * admin's to change — and deliberately not its own text. The text lives in the
 * translation catalogues and is assembled here, at render time, in whatever
 * language the reader has chosen.
 *
 * That split is the point. Help used to be a page of its own, translated into
 * fifteen languages. Turning it into an ordinary message would have meant
 * storing it in one column, in one language, and handing thirteen plants an
 * English guide to a portal they are reading in their own. The server keeps a
 * short English title as a fallback for the admin list and for any client that
 * has no catalogue entry; everything an employee reads comes from here.
 *
 * Adding one: add the row to `BUILTIN_NOTICES` in the backend's
 * `services/messages.py`, add its `builtin:<key>` case below, and add the copy
 * to the `messages` namespace in every catalogue.
 */

import type { TFunction } from "i18next";

export interface BuiltinContent {
  title: string;
  /** Markdown, rendered by the same renderer every other notice uses. */
  body: string;
}

/**
 * Resolve a built-in notice's translated text, or null if it is not one.
 *
 * Args:
 *   externalId: the row's `external_id`, e.g. `builtin:help`.
 *   t: the `assistant` namespace translator — help copy has lived there since
 *      it was a page, and moving the keys would have churned fifteen
 *      catalogues for no reader-visible gain.
 */
export function builtinContent(
  externalId: string | null | undefined,
  t: TFunction<"assistant">,
): BuiltinContent | null {
  if (externalId !== "builtin:help") {
    return null;
  }

  // The guide itself is a component (`HelpGuide`), rendered by the detail
  // page. The body here is the one-line summary, which is also what tells the
  // Message Center card that there is more to read.
  return {
    title: t("help.title"),
    body: t("help.subtitle"),
  };
}
